import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useNavigation } from '../../utilities/NavigationContext';
import { useAuth } from '../../utilities/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AgreementModal from './AgreementModal';

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

class Login extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      showAgreementModal: false,
      pendingAgreements: [],
      currentAgreement: null,
      // we'll park the login payload here while user is signing the agreements
      tempAuthData: null,
      emailNotVerified: false
    };
  }

  componentDidMount() {
    if (this.props.auth?.isLoggedIn) {
      this.props.navigate('/planning');
    }
  }

  // keep this, but it won't fire early anymore because we won't setAuth early
  componentDidUpdate(prevProps) {
    if (!prevProps.auth?.isLoggedIn && this.props.auth?.isLoggedIn) {
      // if modal still showing, don't move
      if (this.state.showAgreementModal) return;
      this.props.navigate('/planning');
    }
  }

  handleInputChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    const { email, password } = this.state;
    const loginEndpoint = `${EndPoint}/user/login`;

    try {
      const response = await axios.post(
        loginEndpoint,
        { email, password },
        { withCredentials: true }
      );

      if (response.status === 200) {
        toast.success('Login Success !', {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
          hideProgressBar: false,
        });

        const backendPending = response.data.pendingAgreements || [];
        const userAgreements = response.data.agreement || [];

        // fallback: build from user.agreement
        let finalPending = backendPending;
        if ((!backendPending || backendPending.length === 0) && userAgreements.length > 0) {
          const needsConsent = userAgreements.filter((ua) => ua.consent === 'no');
          finalPending = needsConsent.map((ua) => ({
            _id: ua.agreementId,
            title: 'User Agreement',
            content:
              'You have a new user agreement to review. Please accept to continue.',
          }));
        }

        // clear local data
        localStorage.removeItem('patientData');

        // 🚨 IMPORTANT PART:
        // If there are agreements, DO NOT setAuth yet.
        if (finalPending.length > 0) {
          // store what we will later put into auth
          const authPayload = {
            isLoggedIn: true,
            credits: response.data.credits,
            role: response.data.role,
            userId: response.data.userId,
            hasSubscription: response.data.hasSubscription || false, 
          };

          this.setState({
            showAgreementModal: true,
            pendingAgreements: finalPending,
            currentAgreement: finalPending[0],
            tempAuthData: authPayload, // we'll use it later
          });

          // ⬆️ DO NOT call this.props.setAuth here
          return;
        }

        // ✅ no agreements → normal flow
        this.props.setAuth((prevAuth) => ({
          ...prevAuth,
          isLoggedIn: true,
          credits: response.data.credits,
          role: response.data.role,
          userId: response.data.userId,
          hasSubscription: response.data.hasSubscription || false,
        }));
        this.props.navigate('/planning');
      } else {
        toast.error('Login Failed !', {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
          hideProgressBar: false,
        });
      }
    } catch (error) {
      console.error(error);
      // 👇 handle email_not_verified HERE
      if (error.response?.status === 401 &&
          error.response?.data?.error === 'email_not_verified') {
        this.setState({ emailNotVerified: true });

        toast.warn(
          error.response?.data?.message || 'Email is not verified yet.',
          {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 4000,
            hideProgressBar: false,
          }
        );
        return; 
      }
      toast.error(
        error?.response?.data?.message || 'There was an error during login !',
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
          hideProgressBar: false,
        }
      );
    }
  };

  // called by AgreementModal when user clicks
  handleAgreementConsent = async (consent) => {
    const { currentAgreement, pendingAgreements, tempAuthData } = this.state;
    if (!currentAgreement) return;

    // if agreement has real _id -> send to backend
    if (currentAgreement._id) {
      try {
        await axios.post(
          `${EndPoint}/user/accept-agreement`,
          {
            agreementId: currentAgreement._id,
            consent,
          },
          { withCredentials: true }
        );
      } catch (err) {
        console.error('Error updating agreement consent', err);
        toast.error('Error saving agreement consent', {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }

    // remove current, move to next
    const remaining = pendingAgreements.filter(
      (ag) => ag._id !== currentAgreement._id
    );

    if (remaining.length > 0) {
      this.setState({
        pendingAgreements: remaining,
        currentAgreement: remaining[0],
        showAgreementModal: true,
      });
    } else {
      // ✅ all agreements done
      this.setState({
        pendingAgreements: [],
        currentAgreement: null,
        showAgreementModal: false,
      });

      // now it's safe to set global auth
      if (tempAuthData) {
        this.props.setAuth((prevAuth) => ({
          ...prevAuth,
          ...tempAuthData,
        }));
      }

      this.props.navigate('/planning');
    }
  };

  resendEmail = async (e) => {
    if (e) e.preventDefault(); // prevent form submit

    const { email } = this.state;

    if (!email) {
      toast.error("Please enter your email first.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    try {
      const response = await axios.post(
        `${EndPoint}/user/resend-verification`,
        { email },
        { withCredentials: true }
      );

      toast.success("Verification email has been resent.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });

      this.setState({ emailNotVerified: false });

    } catch (err) {
      console.error("Resend verification error:", err);

      toast.error(
        err.response?.data?.message || "Unable to resend verification email.",
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        }
      );
    }
  };


  render() {
    const { showAgreementModal, currentAgreement } = this.state;

    return (
      <div>
        <div className="flex flex-col items-center h-[1800px] bg-gradient-to-b from-sky-900 to-sky-800 ">
          <ToastContainer />
          <form onSubmit={this.handleSubmit}>
            <h2 className="text-3xl my-10 text-sky-300 font-semibold text-center">
              Login
            </h2>
            <div className="w-[600px] p-0.5 rounded-xl bg-gradient-to-r from-sky-600 to-sky-800 xs:w-[400px] sm:w-[500px] z-40">
              <div className="flex flex-col p-10 gap-6 w-auto rounded-xl bg-sky-900">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={this.state.email}
                  onChange={this.handleInputChange}
                  required
                  className="rounded border border-sky-700 bg-sky-800 p-4 text-sky-100 text-sm placeholder:text-sky-400 focus:border-sky-600 z-40"
                />
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={this.state.password}
                  onChange={this.handleInputChange}
                  required
                  className="rounded border border-sky-700 bg-sky-800 p-4 text-sky-100 text-sm placeholder:text-sky-400 focus:border-sky-600 z-40"
                />

                <p tabIndex="-1" className="text-sky-400">
                  Forgot your password?{' '}
                  <button
                    type="button"
                    tabIndex="-1"
                    onClick={() => {
                      this.props.navigate('/forgotpassword');
                    }}
                    className="text-red-400 hover:text-red-500"
                  >
                    Reset here
                  </button>
                </p>
                <div className="text-center">
                  {this.state.emailNotVerified ? (
           
                    <button
                      className="text-white bg-sky-600 hover:bg-sky-700 rounded shadow-sm w-auto p-2 my-1 mx-2"
                      onClick={this.resendEmail}
                    >
                      Resend Verification Email
                    </button>
           
                  ):(
                  <button
                    type="submit"
                    className="text-white bg-sky-600 hover:bg-sky-700 rounded shadow-sm w-32 p-2 m-1"
                  >
                    Login
                  </button>
                  )}
                 
                </div>
              </div>
            </div>
          </form>
          <div className="py-4 inline-block text-sky-400">
            Don't have an account?{' '}
            <div className="text-red-400 hover:text-red-500">
              <Link to="/register">Click here to register.</Link>
            </div>
          </div>
      
        </div>

        <AgreementModal
          isOpen={showAgreementModal}
          agreement={currentAgreement}
          onConsent={this.handleAgreementConsent}
        />
      </div>
    );
  }
}

// HOC
const LoginWithNavigation = (props) => {
  const navigate = useNavigation();
  const [auth, setAuth] = useAuth();
  return (
    <Login
      {...props}
      navigate={navigate}
      auth={auth}
      setAuth={setAuth}
    />
  );
};

export default LoginWithNavigation;
